import { Middleware } from 'redux';
import { ActionTypes } from '../../Actions/Actions';
import Core, { gameStarted } from '../../../Core/Core';
import { getLoadedBoard } from '../../../Config/Levels';
import { DDNAEvents, ParamGroups } from '../../../DeltaTracking/Tracker';
import { Views } from '../../reducers/view';
import { TGameAreas } from '../../../DeltaTracking/ResourceParamBuilder';
import { ItemIDs } from '../../../Config/Items';
import ResourceTracking from '../../../Managers/Tracking/ResourceTracking';

// const logger: ILogger = getLogger('[SAVE_ACTIONS]:');

export const deltaActions: Middleware = store => next => action => {
    const oldState = store.getState();
    next(action);
    const newState = store.getState();

    if (!gameStarted) {
        return newState;
    }

    const oldSavedValues = Core.delta.trackCurPropGetters.get(ParamGroups.SAVED)();
    const oldMissionValues = Core.delta.trackCurPropGetters.get(ParamGroups.MISSION)();
    const oldSessionValues = Core.delta.trackCurPropGetters.get(ParamGroups.SESSION)();
    const oldTrackValues = {
        ...oldMissionValues,
        ...oldSavedValues,
        ...oldSessionValues,
    };

    const updatedValues: Map<string, unknown> = new Map();

    if (action.type === ActionTypes.START_LEVEL) {
        setMissionStart();
    }

    saveMissionTrackData();
    saveSessionTrackData();
    saveStoredTrackData();
    saveStoredTrackData();

    saveToTracking(updatedValues);

    sendStateTrackEvents();

    return;

    function setMissionStart() {
        const previousLevelData = oldState.StatePlayer.mapLevels[newState.StateLevel.levelId];
        Core.delta.trackCurPropSetters.get(ParamGroups.MISSION)({
            index: newState.StateLevel.levelIndex,
            id: newState.StateLevel.levelId,
            data: getLoadedBoard(),
            wasCompleted: previousLevelData && previousLevelData.stars !== 0,
            retries:
                previousLevelData && previousLevelData.attempts ? previousLevelData.attempts : 0,
        });
    }

    function saveMissionTrackData(): void {
        if (store.getState().StateView !== Views.GAME) {
            return;
        }

        if (action.type === ActionTypes.ACTIVATE_BOOSTER) {
            const propName = getBoosterrackingProps(action.payload);
            setIncrementalValue(propName, 1);
        }

        if (action.type === ActionTypes.SHUFFLE_TILES) {
            setIncrementalValue('mShuffleUsed', 1);
        }

        if (action.type === ActionTypes.UPDATE_COINS && action.payload < 0) {
            setIncrementalValue('mCoinsSpent', action.payload * -1);

            if (newState.StateLevel.stuck) {
                setIncrementalValue('mLossAversionCoinsSpent', action.payload * -1);
            }
        }

        if (
            action.type === ActionTypes.COUNT_OBJECTS ||
            action.type === ActionTypes.REGISTER_MOVE
        ) {
            const percentComplete =
                (newState.StateLevel.goal.total / newState.StateLevel.goal.target) * 100;
            replaceValue('mGoalsCompletePercentage', percentComplete);
        }

        updatedValues.set('mPossibleMovesLeft', newState.StateBoard.availableMatches.length);
        replaceValue('mPossibleMovesLeft', newState.StateBoard.availableMatches.length);
    }

    function saveSessionTrackData(): void {
        if (action.type === ActionTypes.LEVEL_COMPLETE) {
            setIncrementalValue('levelsPassedThisSession', 1);
        }

        if (action.type === ActionTypes.START_LEVEL) {
            setIncrementalValue('levelsAttemptsThisSession', 1);
            replaceValue('lastLevelAttempted', newState.StateLevel.levelIndex);
        }

        if (action.type === ActionTypes.ACTIVATE_BOOSTER) {
            setIncrementalValue('boostersUsedThisSession', 1);
        }

        if (action.type === ActionTypes.UPDATE_COINS && action.payload < 0) {
            if (action.payload < 0) {
                setIncrementalValue('coinsSpentThisSession', action.payload * -1);
            } else {
                setIncrementalValue('coinsEarnedThisSession', action.payload);
            }
        }
    }

    function saveStoredTrackData(): void {
        if (action.type === ActionTypes.ADD_INVITATION) {
            setIncrementalValue('numFriendsInvited', 1);
        }

        if (action.type === ActionTypes.UPDATE_COINS && action.payload < 0) {
            setIncrementalValue('coinsSpent', action.payload * -1);
            setIncrementalValue('coinPurchasesMade', 1);
        }

        if (action.type === ActionTypes.LEVEL_COMPLETE) {
            replaceValue('lastLevelWon', newState.StateLevel.levelIndex);
            setIncrementalValue('consecutiveWinCount', 1);

            if (oldSavedValues.consecutiveWinCount + 1 > oldSavedValues.consecutiveWinHighest) {
                setIncrementalValue('consecutiveWinHighest', 1);
            }
        }

        if (action.type === ActionTypes.CHANGE_VIEW && oldState.StateLevel.stuck) {
            replaceValue('consecutiveWinCount', 0);
        }
    }

    function saveToTracking(keys: Map<string, unknown>) {
        Core.delta.updateFromState(keys);
    }

    function sendStateTrackEvents() {
        if (action.type === ActionTypes.START_LEVEL) {
            Core.delta.addTrackedEvent(DDNAEvents.MISSION_STARTED);
        }

        if (action.type === ActionTypes.LEVEL_COMPLETE) {
            // Core.delta.addTrackedEvent(DDNAEvents.MISSION_COMPLETED);
        }

        if (action.type === ActionTypes.CHANGE_VIEW && oldState.StateLevel.stuck) {
            Core.delta.addTrackedEvent(DDNAEvents.MISSION_FAILED);
        }

        if (action.type === ActionTypes.ACTIVATE_BOOSTER) {
            // will need to change gameArea if we use same redux action for prelevel boosters
            ResourceTracking.sendUsedResourceEvent(action.payload, 1, TGameAreas.LEVEL);
        }
    }

    function getBoosterrackingProps(payload: string | number) {
        switch (payload) {
            case ItemIDs.HINT:
                return 'mMagnifyingGlassUsed';
            case ItemIDs.HIGHLIGHT:
                return 'mFlashLightUsed';
            case ItemIDs.FIRECRACKER:
                return 'mFireCrackerUsed';
            case ItemIDs.MAGNET:
                return 'mMagnetUsed';
            default:
                return '';
        }
    }

    function setIncrementalValue(propName: string, addVal: number): void {
        const old: any = oldTrackValues;
        const a = old[propName] + addVal;
        updatedValues.set(propName, a);
    }

    function replaceValue(propName: string, newVal: number): void {
        updatedValues.set(propName, newVal);
    }
};
